import { mergeClasses, Tooltip } from '@fluentui/react-components';
import { Star12Filled, Info12Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useSpaceStatusBadgeStyles } from './SpaceStatusBadge.styles';
import type { SpaceDayAvailability } from '@store/models';
import { formatIsoDateForRegion } from '@utils/dateFormat';

/**
 * Booking status of a space (used by SpaceCardProps).
 * Does NOT include "recommended" — that is a separate boolean flag (isRecommended).
 */
export type SpaceStatus = 'available' | 'booked';

/**
 * Visual variant for the SpaceStatusBadge component.
 * Includes "recommended" which is an informative flag, not a booking state.
 */
export type SpaceBadgeVariant = 'recommended' | 'available' | 'booked';

export interface SpaceStatusBadgeProps {
  status: SpaceBadgeVariant;
  spaceAvailability?: SpaceDayAvailability[];
  totalDays?: number;
  className?: string;
}

const statusI18nKeys: Record<SpaceBadgeVariant, string> = {
  recommended: 'spaceCard.recommended',
  available: 'spaceCard.available',
  booked: 'spaceCard.booked',
};

function formatDate(isoDate: string): string {
  return formatIsoDateForRegion(isoDate).split('/').slice(0, 2).join('/');
}

function formatDateList(dates: string[]): string {
  if (dates.length === 0) return '';
  if (dates.length === 1) return dates[0]!;
  if (dates.length === 2) return `${dates[0]} and ${dates[1]}`;
  return `${dates.slice(0, -1).join(', ')} and ${dates[dates.length - 1]}`;
}

export const SpaceStatusBadge = ({
  status,
  spaceAvailability,
  totalDays,
  className,
}: SpaceStatusBadgeProps) => {
  const styles = useSpaceStatusBadgeStyles();
  const { t } = useTranslation('spaceCard');

  const availableDays = spaceAvailability?.filter((d) => d.available);
  const availableCount = availableDays?.length ?? 0;
  const isPartiallyAvailable =
    status === 'available' &&
    spaceAvailability &&
    spaceAvailability.length > 0 &&
    totalDays !== undefined &&
    availableCount > 0 &&
    availableCount < totalDays;

  const label = isPartiallyAvailable
    ? t('spaceCard.availableForDays', { count: availableCount })
    : t(statusI18nKeys[status]);

  const tooltipContent = isPartiallyAvailable
    ? t('spaceCard.availabilityTooltip', {
        dates: formatDateList(availableDays!.map((d) => formatDate(d.dayStartTime))),
      })
    : null;

  const badge = (
    <span
      role="img"
      aria-label={label}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={isPartiallyAvailable ? 0 : undefined}
      className={mergeClasses(
        styles.root,
        styles[status],
        isPartiallyAvailable && styles.interactive,
        className
      )}
    >
      {status === 'recommended' && (
        <span className={styles.starIcon} aria-hidden="true">
          <Star12Filled />
        </span>
      )}
      {label}
      {isPartiallyAvailable && (
        <span className={styles.infoIcon} aria-hidden="true" data-testid="availability-info-icon">
          <Info12Regular />
        </span>
      )}
    </span>
  );

  if (tooltipContent) {
    return (
      <Tooltip content={tooltipContent} relationship="description" positioning="below" withArrow>
        {badge}
      </Tooltip>
    );
  }

  return badge;
};

SpaceStatusBadge.displayName = 'SpaceStatusBadge';
